'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, guardedClient as request, constructQuery } from '@repo/shared/helpers';
import SubcontractingTab from 'src/parts/tabs/SubcontractingTab';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getEvent } from 'src/api/personal/events';
import { getEventTypes } from 'src/api/admin/types';
import { getAllTags } from 'src/api/admin/tags';
import { getSubEventsForEvent } from 'src/api/personal/eventDetail';

interface SubcontractingSearch {
	page?: number;
	sort?: string;
}

export const Route = createFileRoute('/personal_/events/$eventId/subcontracting')({
	validateSearch: (search: Record<string, unknown>): SubcontractingSearch => ({
		page: (search.page as number) || undefined,
		sort: search.sort as string | undefined,
	}),
	component: SubcontractingPage,
});

function SubcontractingPage() {
	const { eventId } = Route.useParams();
	const { page, sort } = Route.useSearch();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { config, loading: configLoading } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const usesTags = featureFlags?.USES_TAGS || false;

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasAllEventsView = verifyPermission([PERMISSIONS.personalAllEventsView]);
	const isOwnEventRoute = !hasAllEventsView;

	const { data: parentEvent, isLoading: eventLoading } = useQuery({
		queryKey: ['personal-event', eventId, isOwnEventRoute],
		queryFn: () => getEvent(eventId, isOwnEventRoute),
		enabled: !authLoading && !!authenticatedUser,
	});

	const query = constructQuery({ page, sort });
	const { data: subEventsData, isLoading: eventsLoading } = useQuery({
		queryKey: ['personal-sub-events', eventId, isOwnEventRoute, page, sort],
		queryFn: () => getSubEventsForEvent(eventId, isOwnEventRoute, query),
		enabled: !!parentEvent,
	});

	const { data: typesData } = useQuery({
		queryKey: ['personal-event-types'],
		queryFn: () => getEventTypes(),
	});

	const { data: tagsData } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
		enabled: usesTags,
	});

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['personal-sub-events', eventId] });
	};

	const addEvent = async (data: unknown) => {
		await request.post(`/api/personal-dashboard/events/${eventId}/subcontracting`, data);
		handleReload();
	};

	const isLoading = authLoading || configLoading || eventLoading || eventsLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!authenticatedUser || !parentEvent) return null;

	const isOwner = !!parentEvent.adminIds?.includes(authenticatedUser.id || '');

	return (
		<SubcontractingTab
			authenticatedUser={authenticatedUser}
			events={subEventsData}
			addEvent={addEvent}
			permissions={{
				view:
					verifyPermission([PERMISSIONS.personalAllEventsView]) ||
					(verifyPermission([PERMISSIONS.personalOwnEventsView]) && isOwner),
				create: verifyPermission([PERMISSIONS.personalAllEventsCreate]),
				update:
					verifyPermission([PERMISSIONS.personalAllEventsEdit]) ||
					(verifyPermission([PERMISSIONS.personalOwnEventsEdit]) && isOwner),
				archive:
					verifyPermission([PERMISSIONS.personalAllEventsArchive]) ||
					(verifyPermission([PERMISSIONS.personalOwnEventsArchive]) && isOwner),
			}}
			onReload={handleReload}
			types={typesData?.data || []}
			tags={tagsData || []}
			usesTags={usesTags}
			parentEventId={eventId}
			parentEvent={parentEvent}
			project={parentEvent?.project}
			eventDetailUrl="/personal/events"
		/>
	);
}
